import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Group, SimpleGrid, Stack, Text, Card, Center, Tooltip, ActionIcon} from "@mantine/core";
import { saveObjective,getAllObjectives, deleteObjective, completeObjective, saveStrategy } from "../actions/improvementPlanActions";
import TitleWithDescription from "../components/TitleWithDescription";
import { IconPlus, IconBulb, IconInfoCircle, IconPlayerTrackPrev, IconFileText } from "@tabler/icons-react";
import { notifyError } from "../components/Toast";
import ObjectiveCard from "../components/Evaluation/ObjectiveCard";
import { usePlanFlags } from "../hooks/usePlanFlags";
import useActiveProcess from "../hooks/useActiveProcess";
import useActivePermission from "../hooks/useActivePermission";
import { usePreviousImprovementPlans } from "../hooks/usePreviousImprovementPlans";
import ImprovementPlanModal from "../components/ModalImprovementPlans";

const ImprovementPlanPage = ({pendingWeakness, navigateTo, editionEnabled = false,}) => {
  
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { data: evaluation, loading: loadingEvaluation } = useSelector((state) => state.evaluation.evaluation);
  const {objectives} = useSelector((state) => state.improvementPlan);
  const creatingObjective = useSelector(s => !!s.improvementPlan.objectives.savingById[`new:objective:${evaluation.Id}`]);

  const activeProcess = useActiveProcess();
  const activePermission = useActivePermission();
  
  const plans = (objectives.data ?? []).sort((a, b) => new Date(b.dt_inserttime) - new Date(a.dt_inserttime))

  // Obtener unidad de gestión y sede
  const unidadGestion = activePermission?.permiso?.EntityId || null;
  const sede = activePermission?.sede?.codigo || null;
  const criterioId = evaluation?.criterio?.Id || null;

  // Hook para obtener planes de mejora previos
  const { data: previousPlans, loading: loadingPrevPlans } = usePreviousImprovementPlans({
    processId: activeProcess?.Id,
    criterioId: criterioId,
    unidad: unidadGestion,
    sede: sede
  });

  const [modalPrevPlanOpened, setModalPrevPlanOpened] = useState(false);
  const [selectedPrevPlan, setSelectedPrevPlan] = useState(null);

  useEffect(() => {
    if(evaluation.Id){
      dispatch(getAllObjectives(evaluation.Id,user));
    }
  }, [evaluation.Id]);

  const handlePartialSaveObjective = async (obj) => {
    if (!obj?.Id) return;
    const noRefetch = { refetch: false };

    await dispatch(saveObjective(evaluation.Id, obj, user, noRefetch));

    const saves = (obj.estrategias || [])
      .filter(e => e.Id)
      .map(e => dispatch(saveStrategy(e, obj.Id, evaluation.Id, user, noRefetch)));

    await Promise.all(saves);

    await dispatch(getAllObjectives(evaluation.Id, user));
  };

  const handleAddObjective = () => {
    const numero = objectives.data?.length > 0 ? objectives.data.length + 1 : 1;
    const newObjective = {
      numero: numero,
      titulo: "Objetivo",
      descripcion: "",
      cualidades: [],
      estrategias: []
    };
    dispatch(saveObjective(evaluation.Id, newObjective, user));
  };

  const handleCompleteObjective = (obj) => {
    if(validateObjective(obj)){
      dispatch(completeObjective(evaluation.Id, obj.Id, user));
    }
  };

  const handleDeleteObjective = (objectiveId) => {
    if (objectiveId) {
      dispatch(deleteObjective(objectiveId, evaluation.Id, user));
    }
  };
  
  const validateObjective = (objective) => {
    
    if (!objective.Id) {
      notifyError("Objetivo invalido");
      return false;
    }
  
    // Validación del objetivo
    if (!objective.descripcion?.trim() || !objective.cualidades?.length) {
      notifyError("Objetivo incompleto. Asegúrate de que tenga una descripción y al menos una cualidad.");
      return false;
    }
  
    if (!objective.estrategias?.length) {
      notifyError("El objetivo debe tener al menos una estrategia completa." );
      return false;
    }
  
    const hasIncompleteStrategy = objective.estrategias.some((estrategia) => 
      !estrategia.descripcion?.trim() ||
      !estrategia.metas?.length ||
      !estrategia.recursos?.length ||
      !estrategia.responsables?.length
    );
  
    if (hasIncompleteStrategy) {
      notifyError( "Una o más estrategias están incompletas.");
      return false;
    }
  
    return true; 
  };

  return(
    <>
      <Box p={'md'}>
        <Stack gap={'sm'}>
          <Group justify="space-between">
            <Text size="lg" fw={600}>Gestion de Objetivos</Text>
            {editionEnabled && (
              <Button 
                color="#E72166" 
                leftSection={<IconPlus style={{width:'70%', height:'70%'}}/>}
                onClick={handleAddObjective} 
                loading={creatingObjective}
              > 
                Nuevo Objetivo
              </Button>
            )}
          </Group>
          <Card withBorder radius={'md'} p={'lg'} style={{borderLeftColor: '#e72166', borderLeftWidth: '4px'}}>
            <SimpleGrid cols={2}>
              <TitleWithDescription 
                icon={<IconInfoCircle size={16}/>}
                size="sm"
                title={'Definición de Objetivo'}
                description={'Los objetivos expresan el resultado que se pretende alcanzar para superar una debilidad detectada. Representan la dirección global y el marco general en el que se planifican las actividades.'}
              />
              <TitleWithDescription 
                icon={<IconBulb size={16}/>}
                size="sm"
                title={'Recomendaciones'}
                description={'Deben estar enunciados de manera concreta, clara y medible en términos de tiempo y grado de cumplimiento. Deben ser realistas y alcanzables, considerando los recursos disponibles. Se formulan con un verbo en infinitivo para indicar acción.  '}
              />
            </SimpleGrid>
          </Card>
          
          {/* Card de Planes de Mejora Previos */}
          {criterioId && unidadGestion && (
            <Card withBorder radius="md" p="md" style={{ background: 'linear-gradient(50deg, #eef5fcff 20%, #c5e3ffff 100%)', borderColor: '#228be6' }}>
              <Group align="flex-start" gap="md">
                <IconPlayerTrackPrev size={28} color="#228be6" style={{ flexShrink: 0, marginTop: 4 }} />
                <div style={{ flex: 1 }}>
                  <Text fw={600} mb={'xs'}>Plan de mejora de la etapa anterior</Text>
                  <Text size="sm">Se muestra el plan de mejora del proceso anterior para este criterio y unidad de gestión.</Text>
                  <Text size="sm">( Se utilizará el proceso previo solo si coincide con la matriz del PEI actual ) </Text>
                  <Box mt={'sm'}>
                    {loadingPrevPlans ? (
                      <Text size="sm" c={'dimmed'} ta={'center'}>Buscando plan de mejora previo…</Text>
                    ) : !previousPlans || previousPlans.length === 0 ? (
                      <Text size="sm" c="dimmed">No se encontró un plan de mejora previo para este criterio.</Text>
                    ) : (
                      <Group gap="md">
                        {previousPlans.map((plan) => {
                          const planTitle = `${plan.titulo || 'Plan'} ${plan.numero}`;
                          return (
                            <Tooltip 
                              label={planTitle} 
                              withArrow 
                              key={plan.Id}
                            >
                              <ActionIcon
                                variant="filled"
                                aria-label="plan"
                                radius="xl"
                                onClick={() => {
                                  setSelectedPrevPlan(plan);
                                  setModalPrevPlanOpened(true);
                                }}
                                color="#E72166"
                                size="lg"
                              >
                                <IconFileText style={{ height: '70%', width: '70%' }} />
                              </ActionIcon>
                            </Tooltip>
                          );
                        })}
                      </Group>
                    )}
                  </Box>
                </div>
              </Group>
            </Card>
          )}

          <Stack gap="md">
            {objectives.loading && !objectives?.data?.length ? (
              <Text ta="center" c="gray" mt="lg">
                Cargando objetivos...
              </Text>
            ) : (plans?.length ?? 0) === 0 ? (
              <Card bg="gray.1" withBorder style={{ borderStyle: "dashed", borderWidth: '2px' }}>
                <Center>
                  <Text c={'dimmed'}>No hay objetivos cargados</Text>
                </Center>
              </Card>
            ) : (
              plans?.map((obj, index) => (
                <ObjectiveCard
                  key={obj.Id ?? index}
                  canEdit={editionEnabled}
                  evaluationId={evaluation.Id}
                  objective={obj}
                  index={index + 1}
                  attributes={evaluation.cualidades}
                  onSave={handlePartialSaveObjective}
                  onComplete={handleCompleteObjective}
                  onDelete={handleDeleteObjective}
                />
              ))
            )}
          </Stack>
        </Stack>
      </Box>
      
      {/* Modal para mostrar el plan de mejora previo */}
      {selectedPrevPlan && (
        <ImprovementPlanModal
          opened={modalPrevPlanOpened}
          onClose={() => {
            setModalPrevPlanOpened(false);
            setSelectedPrevPlan(null);
          }}
          idEvaluacion={null}
          planData={selectedPrevPlan}
          isReadOnly={true}
        />
      )}
    </>
  );
}

export default ImprovementPlanPage;